// Parts list — M2 Phase 5.3 surface. Reads /parts via crm.ts.
//
// Features:
//   - Search box (debounced 200ms) — client-side filter on number +
//     description (crm.ts has no searchParts(); KMF's part count is in
//     the hundreds, so it's fine to filter the full list in-memo).
//   - Part type filter chips (All / Machined / Cut Blank / Weldment /
//     Hybrid / Purchased) with live counts.
//   - Responsive card grid (sm:1 / md:2 / lg:3 col) — each card shows
//     the type badge, part number, description, notes preview, and
//     qty-tier pills.
//   - "+ New part" → NewPartModal (picker → basics → createPart).
//   - Cards have a hover affordance + click handler that alerts a TODO
//     (the part detail page is a future agent's responsibility — see
//     task brief). Keeps the cards interactive so the design reads
//     correctly during M2.5 review.
//
// Empty / loading / error states mirror CustomersPage exactly.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listParts } from '@/shared/crm';
import type { Part, PartType } from '@/shared/crmTypes';
import { PartTypeBadge, PART_TYPE_META } from '@/components/PartTypeBadge';
import { NewPartModal } from '@/components/NewPartModal';
import { PartCardImage } from '@/components/PartCardImage';

type TypeFilter = PartType | 'all';

const TYPE_FILTERS: Array<{ key: TypeFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'machined', label: 'Machined' },
  { key: 'cut_blank', label: 'Cut Blank' },
  { key: 'weldment', label: 'Weldment' },
  { key: 'hybrid', label: 'Hybrid' },
  { key: 'purchased', label: 'Purchased' },
];

export function PartsPage() {
  const navigate = useNavigate();
  const [parts, setParts] = useState<Part[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [modalOpen, setModalOpen] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const data = await listParts();
      setParts(data);
    } catch (e) {
      setError(`Failed to load parts: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  // Debounce search input → searchQuery.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchQuery(searchInput), 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // Apply search + type filter client-side. crm.ts has no substring search
  // for parts and the dataset is small (hundreds at most), so in-memo
  // filtering is fine and keeps the UI snappy without extra round trips.
  const filtered = useMemo(() => {
    const needle = searchQuery.trim().toLowerCase();
    return parts.filter((p) => {
      if (typeFilter !== 'all' && p.type !== typeFilter) return false;
      if (!needle) return true;
      return (
        p.number.toLowerCase().includes(needle) ||
        p.description.toLowerCase().includes(needle)
      );
    });
  }, [parts, searchQuery, typeFilter]);

  function handleCreated(id: string) {
    setModalOpen(false);
    // Land straight on the new part's detail page so the user can keep
    // filling in spec via the wizard without a second click.
    navigate(`/parts/${id}`);
  }

  function handleCardClick(p: Part) {
    // Detail page now lives at /parts/:id (PartDetailPage).
    navigate(`/parts/${p.id}`);
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h1 className="text-2xl font-bold text-navy">Parts</h1>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90 transition-opacity"
        >
          + New part
        </button>
      </div>

      {/* Search + filter row */}
      <div className="bg-paper border border-line rounded p-3 mb-4">
        <div className="flex gap-2 items-center mb-3">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by part number or description…"
            className="flex-1 px-3 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="text-xs text-text-mute hover:text-orange whitespace-nowrap px-2"
          >
            {loading ? 'Loading…' : '↻ Refresh'}
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {TYPE_FILTERS.map((t) => {
            const active = typeFilter === t.key;
            const count =
              t.key === 'all'
                ? parts.length
                : parts.filter((p) => p.type === t.key).length;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTypeFilter(t.key)}
                className={`text-xs px-3 py-1 rounded-full font-semibold transition-colors ${
                  active
                    ? 'bg-orange text-white'
                    : 'bg-bg text-text-mute border border-line-dark hover:border-orange hover:text-orange'
                }`}
              >
                {t.label}
                <span className="ml-1 opacity-70">({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm mb-4 flex items-center justify-between gap-3">
          <span>⚠ {error}</span>
          <button
            type="button"
            onClick={refresh}
            className="text-xs text-orange font-semibold hover:underline shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      {loading && parts.length === 0 && (
        <div className="bg-paper border border-line rounded p-8 text-center text-text-mute">
          <div className="animate-pulse">Loading parts…</div>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && parts.length === 0 && (
        <div className="bg-paper border border-line rounded p-8 text-center">
          <div className="text-4xl mb-2">📐</div>
          <div className="font-semibold text-navy mb-1">No parts yet</div>
          <p className="text-sm text-text-mute mb-4">
            Reusable part definitions — referenced by quotes via partIds[]. Five
            types: machined, cut blank, weldment, hybrid, purchased.
          </p>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90"
          >
            + Create your first part
          </button>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && parts.length > 0 && (
        <div className="bg-paper border border-line rounded p-6 text-center text-text-mute">
          <div className="text-sm">
            No parts match{' '}
            {searchQuery.trim() ? (
              <span className="font-mono">"{searchQuery}"</span>
            ) : (
              <span>this filter</span>
            )}
            .
          </div>
          <button
            type="button"
            onClick={() => {
              setSearchInput('');
              setTypeFilter('all');
            }}
            className="text-xs text-orange font-semibold hover:underline mt-2"
          >
            Clear filters
          </button>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <PartCard key={p.id} part={p} onClick={() => handleCardClick(p)} />
          ))}
        </div>
      )}

      <NewPartModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  );
}

interface PartCardProps {
  part: Part;
  onClick: () => void;
}

function PartCard({ part, onClick }: PartCardProps) {
  // Defensive: a part with an unmapped type (legacy data, schema drift)
  // would otherwise crash the entire list. Mirror the TierBadge fallback.
  const meta = PART_TYPE_META[part.type] ?? {
    type: part.type,
    label: 'Unknown',
    shortLabel: 'Unknown',
    description: 'Type not recognized — fix on detail page',
    icon: '?',
    colorClass: 'steel' as const,
    wizardSteps: [],
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-paper border border-line rounded overflow-hidden hover:border-orange hover:shadow-sm transition-all flex flex-col w-full max-w-sm"
    >
      {/* Lead visual — full-card-width PDF/image/CAD-placeholder/SVG.
          staticVisual + no onClick → the inner visual is purely decorative;
          the outer <button> handles the click for the whole card. Avoids
          the nested-interactive-element trap. The detail page's Graphics
          tab has the real PDF viewer. */}
      <PartCardImage
        part={part}
        width={320}
        staticVisual
        className="border-0 rounded-none w-full"
      />

      {/* Condensed metadata under the image */}
      <div className="p-3 border-t border-line">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-bold text-navy font-mono truncate" title={part.number}>
              {part.number}
            </div>
            <div
              className="text-xs text-text-mute mt-0.5 line-clamp-2"
              title={part.description}
            >
              {part.description}
            </div>
          </div>
          <PartTypeBadge type={part.type} size="sm" />
        </div>

        <div className="flex items-center justify-between mt-2 pt-2 border-t border-line gap-2">
          <div className="flex flex-wrap gap-1">
            {part.qtyTiers?.length > 0 ? (
              part.qtyTiers.map((qty, i) => (
                <span
                  key={`${qty}-${i}`}
                  className="text-[10px] bg-bg border border-line-dark rounded-full px-1.5 py-0.5 font-mono text-text-mute"
                >
                  {qty}
                </span>
              ))
            ) : (
              <span className="text-[11px] text-text-mute italic">no tiers</span>
            )}
          </div>
          <span className="text-[10px] text-text-mute uppercase tracking-wide shrink-0">
            {meta.shortLabel}
          </span>
        </div>
      </div>
    </button>
  );
}
